import { apiUrl } from '../Config';
import { notifyUnauthorized, storedToken } from './session';
import { SceneRow, SceneApiResponse, Folder, MediaFolder, StoryApiResponse, PublishedStorySummary, PublishStoryResult, PublishedStoryStatus, PublishedSceneSummary, PublishSceneResult, PublishedSceneStatus, PictureKind, CharacterFraming, StageView, AssistantView, SheetCut } from '../../../Shared/src/@types';
import type { MediaItem, MediaUsage } from '../@types/Media';
import type {
  ImageJobView, ImageMode, ImageSettingsView, Look, LookOrigin, LookSampleView, MonthlyLimitDetails, StoryLookView,
} from '../../../Shared/src/images';
import type { OperationStep, PlainChange, SceneDocument } from '../../../Shared/src/operations';
import type {
  ApplyStoryOperationsData, RevertStoryOperationsData, StoryOperationError, StoryRevert,
} from '../../../Shared/src/storyOperations/types';

export type ApiSuccess<T> = { success: true; data: T; error?: undefined };
/** `status` is 404 when the server said the resource does not exist. */
export type ApiFailure = { success: false; data?: any; error: string; status?: number };
export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

/** The apply route's 409 (spec 023) also names the failing step. */
export type StoryOperationsResponse<T> =
  | ApiSuccess<T>
  | (ApiFailure & { failedIndex?: number; errors?: StoryOperationError[] });

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** Per-call options. `signal` lets an effect discard a request it no longer wants. */
export interface RequestOptions {
  signal?: AbortSignal;
}

/** `GET /folders/:id/summary`: what a folder delete touches (spec 002). */
export interface FolderSummary {
  folders: number;
  scenes: number;
  stories: number;
  /** Stories outside the folder that use scenes inside it. */
  outsideStories: { id: number; name: string }[];
}

/** A failed image route (spec 027): a 429 also carries the limit's details. */
export type ImageApiResponse<T> = ApiSuccess<T> | (ApiFailure & Partial<MonthlyLimitDetails>);

/** `PUT /images/settings` body (spec 027). `byokKey: ''` removes the key; `monthlyLimit: 0` clears the admin's limit. */
export interface ImageSettingsUpdate { mode?: ImageMode; byokKey?: string; monthlyLimit?: number }

/** `PUT /stories/:id/look` body (spec 027): always the story's own look (D-20). */
export interface LookUpdate {
  /** A preset's id when its text is unchanged, `own` for "Your own style", absent when a preset was edited. */
  presetId?: LookOrigin;
  text: string;
  palette?: string[];
  confirm?: boolean;
}

/** `POST /stories/:id/look/sample` body (spec 027): a wish (the AI writes or revises the text), or a text alone. */
export interface LookSampleRequest { wish?: string; text?: string }

/** `POST /stories/:id/image-jobs` body (spec 027); `framing` for characters (D-22). */
export interface ImageJobsRequest {
  slots: { slotId: string; brief?: string; framing?: CharacterFraming }[];
  count?: number;
  replace?: boolean;
}

/** `GET /agent/status` (spec 022). */
export interface AgentStatus { available: boolean }

/** One prior turn's text, as the assistant's `history` carries it (spec 022). */
export interface AgentHistoryEntry { role: 'user' | 'assistant'; text: string }

/** `POST /scenes/:id/agent/turn` body (spec 022). */
export interface AgentTurnRequest {
  message: string;
  history: AgentHistoryEntry[];
  document: SceneDocument;
  /** Spec 031 v3: what the author is looking at when the turn is sent. */
  view?: AssistantView;
}

/**
 * One successful operation of a turn: replayable with `applyOperations` (spec 021).
 * `change` is the step in plain words (spec 028); absent from a Service older than 028.
 */
export interface AgentTurnStep extends OperationStep { summary: string; change?: PlainChange[] }

/** `POST /scenes/:id/agent/turn` data (spec 022). */
export interface AgentTurnResult {
  reply: string;
  steps: AgentTurnStep[];
  usage: { inputTokens: number; cacheReadTokens: number; outputTokens: number; iterations: number };
  /** Spec 030: a scene turn's summary of the scene's own picture map; present when it had steps and `total > 0`. */
  pictures?: StoryTurnPictures;
  /** Spec 031: the turn's looks at the stage, in order; absent when it did not look. */
  views?: StageView[];
}

/** `POST /stories/:id/agent/turn` body (spec 024): no document, the Service reads the saved story. */
export interface StoryAgentTurnRequest {
  message: string;
  history: AgentHistoryEntry[];
  /** The `StoryScene.id` the author is looking at (025's selected scene; the story map's selected node, 031 D-18). */
  selectedSceneId?: number;
  /** Spec 031 v3: what the author is looking at when the turn is sent. */
  view?: AssistantView;
}

/**
 * One sound the story turn could not cast from the library (spec 024). `scene`
 * is a scene name. Since spec 026 (D-12) only `kind: 'sound'` occurs: pictures
 * go into the story's picture map.
 */
export interface MissingMediaItem {
  scene?: string;
  kind: 'character' | 'background' | 'sound' | 'other';
  description: string;
}

/** `POST /stories/:id/agent/turn` data (spec 024): steps for `storyStore.applyOperations`. */
export interface StoryAgentTurnResult extends AgentTurnResult {
  missingMedia: MissingMediaItem[];
  /** Spec 026: the picture map's summary after the turn; present when it had steps and `total > 0`. */
  pictures?: StoryTurnPictures;
}

/** Spec 026's turn summary of the picture map: counts, and the needed pictures grouped by subject. */
export interface StoryTurnPictures {
  total: number;
  filled: number;
  needed: { kind: PictureKind; subject: string; variants: string[] }[];
}

/** The only calls whose 401 means "wrong credentials", not "session ended". */
const CREDENTIAL_PATHS = new Set(['/auth/login', '/auth/register']);

export class ApiClient {
  private tokenProvider: () => string | undefined;
  private baseUrl: string;

  constructor(opts: {
    baseUrl: string;
    tokenProvider: () => string | undefined;
  }) {
    this.baseUrl = opts.baseUrl.replace(/\/$/, '');
    this.tokenProvider = opts.tokenProvider;
  }

  setTokenProvider(provider: () => string | undefined) {
    this.tokenProvider = provider;
  }

  setToken(token: string | undefined) {
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  private buildUrl(path: string) {
    if (!path.startsWith('/')) path = `/${path}`;
    return `${this.baseUrl}${path}`;
  }

  private headers(token: string | undefined, extra?: HeadersInit): HeadersInit {
    const base: Record<string, string> = {
      Accept: 'application/json',
    };

    if (token) {
      base['X-Auth-Token'] = token;
    }

    return {
      ...base,
      ...(extra || {}),
    };
  }

  /**
   * Any 401 on an authenticated call ends the session (spec 001): the keys are
   * cleared, the stores reset and AuthContext sends the user to sign in. The
   * caller still gets its `{ success: false, error }` / thrown error as before.
   */
  private handleStatus(res: Response, path: string, token: string | undefined) {
    if (res.status === 401 && !CREDENTIAL_PATHS.has(path.split('?')[0])) {
      notifyUnauthorized(token);
    }
  }

  private async requestJson<T>(
    method: HttpMethod,
    path: string,
    body?: any,
    opts?: RequestOptions
  ): Promise<ApiResponse<T>> {
    const token = this.tokenProvider();
    const init: RequestInit = {
      method,
      signal: opts?.signal,
      headers: this.headers(
        token,
        body instanceof FormData
          ? undefined
          : { 'Content-Type': 'application/json' }
      ),
      body:
        body === undefined
          ? undefined
          : body instanceof FormData
            ? body
            : JSON.stringify(body),
    };

    const res = await fetch(this.buildUrl(path), init);
    this.handleStatus(res, path, token);

    const contentType = res.headers.get('content-type') || '';
    const isJson = contentType.includes('application/json');

    if (!isJson) {
      // Common when the Service isn't running / wrong port / stale server returns HTML error page.
      const text = await res.text().catch(() => '');
      const snippet = text.slice(0, 200).replace(/\s+/g, ' ').trim();
      return {
        success: false,
        error: `Expected JSON from ${method} ${path} but got ${contentType || 'unknown content-type'} (HTTP ${res.status}). ${snippet}`,
      };
    }

    try {
      const json = (await res.json()) as ApiResponse<T>;
      // A 404 is told apart so a missing resource can say "not found" (spec 004).
      if (res.status === 404 && json && typeof json === 'object' && json.success === false) {
        return { ...json, status: 404 };
      }
      return json;
    } catch (e) {
      return {
        success: false,
        error: `Failed to parse JSON from ${method} ${path} (HTTP ${res.status}).`,
      };
    }
  }

  private async requestBlob(method: HttpMethod, path: string, opts?: RequestOptions): Promise<Blob> {
    const token = this.tokenProvider();
    const res = await fetch(this.buildUrl(path), {
      method,
      signal: opts?.signal,
      headers: this.headers(token),
    });
    this.handleStatus(res, path, token);

    if (!res.ok) {
      // try to surface service error body if any
      let message = `HTTP ${res.status}`;
      try {
        const json = await res.json();
        if (json?.error) message = String(json.error);
      } catch {
        // ignore
      }
      throw new Error(message);
    }

    return await res.blob();
  }

  // Auth
  register(email: string, password: string) {
    return this.requestJson<{ id: number; email: string; token: string }>(
      'POST',
      '/auth/register',
      { email, password }
    );
  }

  login(email: string, password: string) {
    return this.requestJson<{ id: number; email: string; token: string }>(
      'POST',
      '/auth/login',
      { email, password }
    );
  }

  /** The signed-in user for the stored token; 401 when it is bad or the user is gone. */
  getMe(opts?: RequestOptions) {
    return this.requestJson<{ id: number; email: string }>('GET', '/auth/me', undefined, opts);
  }

  // Scenes
  getScenes(opts?: RequestOptions) {
    return this.requestJson<SceneApiResponse[]>('GET', '/scenes', undefined, opts);
  }

  createScene(payload?: { name?: string; folderId?: number | null }) {
    return this.requestJson<SceneApiResponse>('POST', '/scenes', payload || {});
  }

  getScene(sceneId: number | string) {
    return this.requestJson<SceneApiResponse>('GET', `/scenes/${sceneId}`);
  }

  updateScene(sceneId: number | string, updates: Partial<SceneApiResponse>) {
    return this.requestJson<SceneApiResponse>('PATCH', `/scenes/${sceneId}`, updates);
  }

  cloneScene(
    sceneId: number | string,
    payload?: { name?: string; cloneAssets?: boolean }
  ) {
    return this.requestJson<SceneApiResponse>(
      'POST',
      `/scenes/${sceneId}/clone`,
      payload || {}
    );
  }

  deleteScene(sceneId: number | string) {
    return this.requestJson<{ success: true }>('DELETE', `/scenes/${sceneId}`);
  }

  getSceneStoryLinks(sceneId: number | string) {
    return this.requestJson<{ sceneId: number; links: { id: number; storyId: number; mode: string; storyName: string }[]; count: number }>(
      'GET',
      `/scenes/${sceneId}/story-links`
    );
  }

  // Folders
  getFolders(opts?: RequestOptions) {
    return this.requestJson<Folder[]>('GET', '/folders', undefined, opts);
  }

  createFolder(payload: { name: string; parentId?: number | null }) {
    return this.requestJson<Folder>('POST', '/folders', payload);
  }

  getFolder(folderId: number | string) {
    return this.requestJson<Folder>('GET', `/folders/${folderId}`);
  }

  updateFolder(folderId: number | string, updates: Partial<Folder>) {
    return this.requestJson<Folder>('PATCH', `/folders/${folderId}`, updates);
  }

  /** `moveUp` (default) re-parents the contents; `deleteAll` deletes the subtree (spec 002). */
  deleteFolder(folderId: number | string, mode: 'moveUp' | 'deleteAll' = 'moveUp') {
    return this.requestJson<{ deleted: { folders: number; scenes: number; stories: number } }>(
      'DELETE',
      `/folders/${folderId}?mode=${mode}`
    );
  }

  /** Recursive contents, for the delete confirmation (spec 002). */
  getFolderSummary(folderId: number | string) {
    return this.requestJson<FolderSummary>('GET', `/folders/${folderId}/summary`);
  }

  // Media Folders
  getMediaFolders(opts?: RequestOptions) {
    return this.requestJson<MediaFolder[]>('GET', '/media-folders', undefined, opts);
  }

  createMediaFolder(payload: { name: string; parentId?: number | null; iconId?: number | null }) {
    return this.requestJson<MediaFolder>('POST', '/media-folders', payload);
  }

  getMediaFolder(folderId: number | string) {
    return this.requestJson<MediaFolder>('GET', `/media-folders/${folderId}`);
  }

  updateMediaFolder(folderId: number | string, updates: Partial<MediaFolder>) {
    return this.requestJson<MediaFolder>('PATCH', `/media-folders/${folderId}`, updates);
  }

  deleteMediaFolder(folderId: number | string, options?: { deleteContents?: boolean; reparentTo?: number | null }) {
    const params = new URLSearchParams();
    if (options?.deleteContents !== undefined) {
      params.set('deleteContents', String(options.deleteContents));
    }
    if (options?.reparentTo !== undefined) {
      params.set('reparentTo', options.reparentTo === null ? 'null' : String(options.reparentTo));
    }
    const queryString = params.toString();
    return this.requestJson<{ success: true }>('DELETE', `/media-folders/${folderId}${queryString ? `?${queryString}` : ''}`);
  }


  // Media
  getMedia(opts?: RequestOptions) {
    return this.requestJson<any[]>('GET', '/media', undefined, opts);
  }

  getMediaById(mediaId: number | string, opts?: RequestOptions) {
    return this.requestJson<any>('GET', `/media/${mediaId}`, undefined, opts);
  }

  uploadMedia(file: File, payload?: { name?: string; description?: string; folderId?: number | null }) {
    const form = new FormData();
    form.append('file', file);
    if (payload?.name !== undefined) form.append('name', payload.name);
    if (payload?.description !== undefined)
      form.append('description', payload.description);
    if (payload?.folderId !== undefined && payload.folderId !== null)
      form.append('folderId', String(payload.folderId));
    return this.requestJson<any>('POST', '/media', form);
  }

  /**
   * Upload one file with real progress (spec 003): XMLHttpRequest, because
   * fetch cannot report upload progress. `onProgress` gets 0-100 as bytes go
   * out; the promise resolves with the server's JSON (or a failure built from
   * the HTTP status) and rejects only when aborted.
   */
  uploadMediaWithProgress(
    file: File,
    payload: { name?: string; folderId?: number | null },
    onProgress: (percent: number) => void,
    opts?: RequestOptions
  ): Promise<ApiResponse<MediaItem>> {
    const form = new FormData();
    if (payload.name !== undefined) form.append('name', payload.name);
    if (payload.folderId !== undefined && payload.folderId !== null) form.append('folderId', String(payload.folderId));
    form.append('file', file);

    const path = '/media';
    const token = this.tokenProvider();
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', this.buildUrl(path));
      xhr.setRequestHeader('Accept', 'application/json');
      if (token) xhr.setRequestHeader('X-Auth-Token', token);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
      };
      xhr.onload = () => {
        if (xhr.status === 401) notifyUnauthorized(token);
        let body: any = null;
        try {
          body = JSON.parse(xhr.responseText);
        } catch {
          body = null;
        }
        if (body && typeof body.success === 'boolean') {
          resolve(body as ApiResponse<MediaItem>);
        } else {
          resolve({ success: false, error: `Upload failed (HTTP ${xhr.status})` });
        }
      };
      xhr.onerror = () => resolve({ success: false, error: 'Upload failed: the server could not be reached' });
      xhr.onabort = () => reject(new DOMException('Upload aborted', 'AbortError'));
      opts?.signal?.addEventListener('abort', () => xhr.abort(), { once: true });
      xhr.send(form);
    });
  }

  /** The caller's scenes and folders that reference a media (spec 003). */
  getMediaUsage(mediaId: number | string, opts?: RequestOptions) {
    return this.requestJson<MediaUsage>('GET', `/media/${mediaId}/usage`, undefined, opts);
  }

  updateMedia(
    mediaId: number | string,
    updates: { name?: string; description?: string; folderId?: number | null; sheet?: SheetCut | false }
  ) {
    return this.requestJson<any>('PATCH', `/media/${mediaId}`, updates);
  }

  deleteMedia(mediaId: number | string) {
    return this.requestJson<{ success: true }>('DELETE', `/media/${mediaId}`);
  }

  getMediaFileBlob(mediaId: number | string, opts?: RequestOptions) {
    return this.requestBlob('GET', `/media/${mediaId}/file`, opts);
  }

  // Stories
  getStories(opts?: RequestOptions) {
    return this.requestJson<StoryApiResponse[]>('GET', '/stories', undefined, opts);
  }

  createStory(payload?: { name?: string; folderId?: number | null }) {
    return this.requestJson<StoryApiResponse>('POST', '/stories', payload || {});
  }

  getStory(storyId: number | string) {
    return this.requestJson<StoryApiResponse>('GET', `/stories/${storyId}`);
  }

  updateStory(storyId: number | string, updates: Partial<StoryApiResponse>) {
    return this.requestJson<StoryApiResponse>('PATCH', `/stories/${storyId}`, updates);
  }

  deleteStory(storyId: number | string, mode: 'orphan' | 'delete' = 'orphan') {
    return this.requestJson<{ success: true }>('DELETE', `/stories/${storyId}?mode=${mode}`);
  }

  cloneStory(storyId: number | string, payload?: { name?: string }) {
    return this.requestJson<StoryApiResponse>('POST', `/stories/${storyId}/clone`, payload || {});
  }

  addSceneReferenceToStory(storyId: number | string, sceneId: number) {
    return this.requestJson<any>('POST', `/stories/${storyId}/scenes/reference`, { sceneId });
  }

  copySceneToStory(storyId: number | string, sceneId: number) {
    return this.requestJson<any>('POST', `/stories/${storyId}/scenes/copy`, { sceneId });
  }

  ownSceneInStory(storyId: number | string, sceneId: number) {
    return this.requestJson<any>('POST', `/stories/${storyId}/scenes/own`, { sceneId });
  }

  removeStoryScene(storyId: number | string, storySceneId: number) {
    return this.requestJson<{ success: true }>('DELETE', `/stories/${storyId}/scenes/${storySceneId}`);
  }

  /** `POST /stories/:id/operations` (spec 023): one batch of story operations, atomic. */
  applyStoryOperations(storyId: number | string, steps: OperationStep[]) {
    return this.requestJson<ApplyStoryOperationsData>('POST', `/stories/${storyId}/operations`, { steps }) as Promise<
      StoryOperationsResponse<ApplyStoryOperationsData>
    >;
  }

  /** `POST /stories/:id/operations/revert` (spec 023): undo one batch; 409 `changed_since` when edited since. */
  revertStoryOperations(storyId: number | string, revert: StoryRevert) {
    return this.requestJson<RevertStoryOperationsData>('POST', `/stories/${storyId}/operations/revert`, { revert });
  }

  // Story and single-scene publish (spec 011): the Service builds every URL.
  getPublishedStories(opts?: RequestOptions) {
    return this.requestJson<PublishedStorySummary[]>('GET', '/stories/published', undefined, opts);
  }

  publishStory(storyId: number | string) {
    return this.requestJson<PublishStoryResult>(
      'POST',
      `/stories/${storyId}/publish`
    );
  }

  /** `GET /scenes/published` (spec 011): the caller's published scenes, newest first. */
  getPublishedScenes(opts?: RequestOptions) {
    return this.requestJson<PublishedSceneSummary[]>('GET', '/scenes/published', undefined, opts);
  }

  /** `POST /scenes/:id/publish` (spec 011): a free scene as a published item of its own. */
  publishScene(sceneId: number | string) {
    return this.requestJson<PublishSceneResult>('POST', `/scenes/${sceneId}/publish`);
  }

  /** `GET /scenes/:id/published` (spec 011): null when never published. */
  getPublishedScene(sceneId: number | string) {
    return this.requestJson<PublishedSceneStatus | null>('GET', `/scenes/${sceneId}/published`);
  }

  // Scene assistant (spec 022)
  getAgentStatus(opts?: RequestOptions) {
    return this.requestJson<AgentStatus>('GET', '/agent/status', undefined, opts);
  }

  /** One assistant turn on the editor's current document; `signal` is the panel's Stop. */
  agentTurn(sceneId: number | string, body: AgentTurnRequest, opts?: RequestOptions) {
    return this.requestJson<AgentTurnResult>('POST', `/scenes/${sceneId}/agent/turn`, body, opts);
  }

  /** One story-assistant turn (spec 024) on the saved story; `signal` is the panel's Stop. */
  storyAgentTurn(storyId: number | string, body: StoryAgentTurnRequest, opts?: RequestOptions) {
    return this.requestJson<StoryAgentTurnResult>('POST', `/stories/${storyId}/agent/turn`, body, opts);
  }

  // Image generation (spec 027)
  getImageSettings(opts?: RequestOptions) {
    return this.requestJson<ImageSettingsView>('GET', '/images/settings', undefined, opts) as Promise<ImageApiResponse<ImageSettingsView>>;
  }

  saveImageSettings(body: ImageSettingsUpdate) {
    return this.requestJson<ImageSettingsView>('PUT', '/images/settings', body) as Promise<ImageApiResponse<ImageSettingsView>>;
  }

  getStoryLook(storyId: number | string, opts?: RequestOptions) {
    return this.requestJson<StoryLookView>('GET', `/stories/${storyId}/look`, undefined, opts);
  }

  saveStoryLook(storyId: number | string, body: LookUpdate) {
    return this.requestJson<{ look: Look; confirmed: boolean }>('PUT', `/stories/${storyId}/look`, body);
  }

  /** One "Your own style" round; a `sample_failed` failure carries the written `text`. */
  sampleLook(storyId: number | string, body: LookSampleRequest, opts?: RequestOptions) {
    return this.requestJson<LookSampleView>('POST', `/stories/${storyId}/look/sample`, body, opts) as Promise<
      ImageApiResponse<LookSampleView> & { text?: string }
    >;
  }

  getLookSampleBlob(sampleId: string, opts?: RequestOptions) {
    return this.requestBlob('GET', `/look-samples/${sampleId}`, opts);
  }

  /** "Write from the story" (spec 027 D-23): a brief for the slot; nothing is saved. */
  writePictureBrief(storyId: number | string, slotId: string) {
    return this.requestJson<{ brief: string }>('POST', `/stories/${storyId}/image-briefs`, { slotId }) as Promise<ImageApiResponse<{ brief: string }>>;
  }

  // A standalone scene's own look, jobs and briefs (spec 030 D-9): the same bodies and answers as the story routes.
  getSceneLook(sceneId: number | string, opts?: RequestOptions) {
    return this.requestJson<StoryLookView>('GET', `/scenes/${sceneId}/look`, undefined, opts);
  }

  saveSceneLook(sceneId: number | string, body: LookUpdate) {
    return this.requestJson<{ look: Look; confirmed: boolean }>('PUT', `/scenes/${sceneId}/look`, body);
  }

  sampleSceneLook(sceneId: number | string, body: LookSampleRequest, opts?: RequestOptions) {
    return this.requestJson<LookSampleView>('POST', `/scenes/${sceneId}/look/sample`, body, opts) as Promise<
      ImageApiResponse<LookSampleView> & { text?: string }
    >;
  }

  /** "Write from the scene" (spec 030): a brief for the slot from the saved scene; nothing is saved. */
  writeScenePictureBrief(sceneId: number | string, slotId: string) {
    return this.requestJson<{ brief: string }>('POST', `/scenes/${sceneId}/image-briefs`, { slotId }) as Promise<ImageApiResponse<{ brief: string }>>;
  }

  startSceneImageJobs(sceneId: number | string, body: ImageJobsRequest) {
    return this.requestJson<{ jobs: { jobId: string; slotId: string }[] }>('POST', `/scenes/${sceneId}/image-jobs`, body) as Promise<
      ImageApiResponse<{ jobs: { jobId: string; slotId: string }[] }>
    >;
  }

  startImageJobs(storyId: number | string, body: ImageJobsRequest) {
    return this.requestJson<{ jobs: { jobId: string; slotId: string }[] }>('POST', `/stories/${storyId}/image-jobs`, body) as Promise<
      ImageApiResponse<{ jobs: { jobId: string; slotId: string }[] }>
    >;
  }

  getImageJob(jobId: string, opts?: RequestOptions) {
    return this.requestJson<ImageJobView>('GET', `/image-jobs/${jobId}`, undefined, opts);
  }

  getDraftBlob(jobId: string, index: number, opts?: RequestOptions) {
    return this.requestBlob('GET', `/image-jobs/${jobId}/drafts/${index}`, opts);
  }

  cancelImageJob(jobId: string) {
    return this.requestJson<ImageJobView>('POST', `/image-jobs/${jobId}/cancel`);
  }

  retryDraft(jobId: string, index: number) {
    return this.requestJson<ImageJobView>('POST', `/image-jobs/${jobId}/drafts/${index}/retry`) as Promise<ImageApiResponse<ImageJobView>>;
  }

  moreDrafts(jobId: string, brief?: string) {
    return this.requestJson<ImageJobView>('POST', `/image-jobs/${jobId}/more`, brief !== undefined ? { brief } : {}) as Promise<ImageApiResponse<ImageJobView>>;
  }

  acceptDraft(jobId: string, index: number, name: string) {
    return this.requestJson<{ mediaId: number; folderId: number }>('POST', `/image-jobs/${jobId}/accept`, { index, name });
  }

  redrawMedia(mediaId: number) {
    return this.requestJson<{ mediaId: number; final: 'drawing' }>('POST', `/media/${mediaId}/redraw`) as Promise<ImageApiResponse<{ mediaId: number; final: 'drawing' }>>;
  }

  getPublishedStory(storyId: number | string) {
    return this.requestJson<PublishedStoryStatus | null>(
      'GET',
      `/stories/${storyId}/published`
    );
  }
}

// The session lives in localStorage (spec 001), so the token is always read
// from there: a replaced or cleared token takes effect on the next request.
const apiClient = new ApiClient({
  baseUrl: apiUrl,
  tokenProvider: storedToken,
});

/** The shared client, as a hook for components that already use it this way. */
export const useApiClient = () => apiClient;

export { apiClient };
